// Copyright (c) Docugami, Inc. All rights reserved.
import { TooltipHost } from '@fluentui/react';
import { primaryTheme } from 'components/shell/presentation/AppStyles';
import { ConfirmationDialog } from 'components/shell/utils/ConfirmationDialog';
import React from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { selectIsLimitedOrganization } from 'store/AccountInfoHelpers';
import { hideUpgradeModal } from 'store/display/actions';
import styled from 'styled-components';
import { useAppDispatch } from 'hooks/redux';
import { usePageLimits } from 'hooks/PageLimits';

const Container = styled.div`
    opacity: .3;
    * {
        pointer-events: none;
    }
`;

interface LimitsContainerProps {
    limitsDisabled?: boolean;
    children?: JSX.Element | JSX.Element[] | null;
    actionName?: string;
}

export const LimitsContainer = (props: LimitsContainerProps): JSX.Element => {
    const disabled = useSelector((state: AppState) => selectIsLimitedOrganization(state));

    return !disabled || !!props.limitsDisabled ? <>{props.children}</> : (
        <TooltipHost content={`You are no longer able to ${props.actionName || 'use this item'}. Please upgrade to unlock this feature.`} calloutProps={{theme: primaryTheme}}>
            <Container>
                {props.children}
            </Container>
        </TooltipHost>
    );
}

export const UploadLimitsContainer = (props: LimitsContainerProps): JSX.Element => {
    const { pageUploadDisabled } = usePageLimits();

    return !pageUploadDisabled ? <>{props.children}</> : (
        <TooltipHost content={`You are no longer able to ${props.actionName || 'upload documents'}. Please upgrade to unlock this feature.`} calloutProps={{theme: primaryTheme}}>
            <Container>
                {props.children}
            </Container>
        </TooltipHost>
    );
}

const ParentContainer = styled.div`
    cursor: default;
    button {
        pointer-events: none;
    }
`;

export const ParentLimitsContainer = (props: LimitsContainerProps): JSX.Element => {
    const isLimitedOrg = useSelector((state: AppState) => selectIsLimitedOrganization(state));

    if (!isLimitedOrg) {
        return <>{props.children}</>;
    }

    return (
        <ParentContainer onMouseDownCapture={(ev?: React.MouseEvent<HTMLDivElement, MouseEvent>) => { ev?.stopPropagation() }} >
            {props.children}
        </ParentContainer>
    )
}

export const UpgradeAccountModal = (): JSX.Element => {
    const upgradeModalProps = useSelector((state: AppState) => state.display.upgradeModalDisplayProps);
    const dispatch = useAppDispatch();

    return (
        <ConfirmationDialog
            hidden={!upgradeModalProps?.visible}
            title={upgradeModalProps?.title}
            subText={upgradeModalProps?.subText}
            confirmButtonText="OK"
            confirmedCallback={() => dispatch(hideUpgradeModal())}
            dismissedCallback={() => dispatch(hideUpgradeModal())} />
    )
}
